/**
 * Was das Löschen eines Kontos mitnimmt, in Sätzen (HM-163, ADR 0044).
 *
 * Die Zahlen kommen vom Server (`GET /auth/me/deletion`); hier entsteht nur die
 * Sprache. **Andere Menschen zuerst** — dass die eigene Anlage weg ist, weiß
 * jeder, der auf „Konto löschen" drückt; dass zwei Mieter danach ihre Werte
 * nicht mehr sehen, weiß er womöglich nicht.
 *
 * Die Mobile-App hat dieselbe Rechnung als Kopie mit Test
 * (`apps/mobile/src/lib/account-deletion.ts`) — Apps importieren sich nicht.
 */
import type { ApiComponents } from "@hausmeter/sdk";

type AccountDeletionPreview = ApiComponents["schemas"]["AccountDeletionPreview"];

export function deletionConsequences(preview: AccountDeletionPreview): string[] {
  const lines: string[] = [];
  if (preview.residents > 0) {
    lines.push(
      preview.residents === 1
        ? "Ein Bewohner verliert den Zugang zu seiner Wohnung."
        : `${preview.residents} Bewohner verlieren den Zugang zu ihren Wohnungen.`,
    );
  }
  if (preview.guests > 0) {
    lines.push(
      preview.guests === 1
        ? "Eine Person, mit der du eine Anlage geteilt haben, sieht sie danach nicht mehr."
        : `${preview.guests} Personen, mit denen du Anlagen geteilt haben, sehen sie danach nicht mehr.`,
    );
  }
  if (preview.members > 0) {
    lines.push(
      preview.members === 1
        ? "Ein weiteres Mitglied deines Accounts verliert den Zugang."
        : `${preview.members} weitere Mitglieder deines Accounts verlieren den Zugang.`,
    );
  }
  if (preview.sites > 0) {
    const sites = preview.sites === 1 ? "Deine Anlage" : `Deine ${preview.sites} Anlagen`;
    lines.push(`${sites} mit allen Zählern, Zählerständen und Verbrauchsdaten wird gelöscht.`);
  }
  if (preview.devices > 0) {
    lines.push(
      preview.devices === 1
        ? "Ein Gerät verliert seine Verbindung zur Cloud; im Heimnetz arbeitet es weiter."
        : `${preview.devices} Geräte verlieren ihre Verbindung zur Cloud; im Heimnetz arbeiten sie weiter.`,
    );
  }
  return lines;
}

/** Warum nicht gelöscht werden darf — oder `null`. */
export function deletionBlockedReason(preview: AccountDeletionPreview): string | null {
  if (preview.blockedBy === "last_platform_admin") {
    return "Dies ist das letzte Admin-Konto. Vergib die Rolle zuerst an jemand anderen — sonst erreicht niemand mehr die Verwaltung.";
  }
  return null;
}
